import dayjs, { Dayjs } from "dayjs";
import { useState, useEffect } from "react";
import quarterOfYear from "dayjs/plugin/quarterOfYear";

// Initialize the quarter plugin
dayjs.extend(quarterOfYear);

// Define standard date range preset type
export type DateRangePreset = {
  label: string;
  value: [Dayjs, Dayjs];
};

// Standard date range type
export type DateRange = [Dayjs, Dayjs];

/**
 * Shared date range presets for consistent usage across the application
 * Each preset returns fresh dayjs objects to ensure dates are current
 */
export const getDateRangePresets = (): DateRangePreset[] => [
  {
    label: "Today",
    value: [dayjs().startOf('day'), dayjs().endOf('day')],
  },
  {
    label: "Yesterday",
    value: [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')],
  },
  {
    label: "2 Days Ago",
    value: [dayjs().subtract(2, 'day').startOf('day'), dayjs().subtract(2, 'day').endOf('day')],
  },
  {
    label: "Last 7 Days",
    value: [dayjs().subtract(7, "day"), dayjs()],
  },
  {
    label: "Last 14 Days",
    value: [dayjs().subtract(14, "day"), dayjs()],
  },
  {
    label: "Last 30 Days",
    value: [dayjs().subtract(30, "day"), dayjs()],
  },
  {
    label: "Last 90 Days",
    value: [dayjs().subtract(90, "day"), dayjs()],
  },
  {
    label: "Last Week",
    value: [dayjs().subtract(1, 'week').startOf('week'), dayjs().subtract(1, 'week').endOf('week')],
  },
  {
    label: "This Month",
    value: [dayjs().startOf('month'), dayjs().endOf('month')],
  },
  {
    label: "Last Month",
    value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')],
  },
  {
    label: "This Quarter",
    value: [dayjs().startOf('quarter'), dayjs()],
  },
  {
    label: "Last Quarter",
    value: [
      dayjs().subtract(1, 'quarter').startOf('quarter'),
      dayjs().subtract(1, 'quarter').endOf('quarter')
    ],
  },
  {
    label: "This Year",
    value: [dayjs().startOf('year'), dayjs()],
  },
  {
    label: "Last Year",
    value: [dayjs().subtract(1, 'year').startOf('year'), dayjs().subtract(1, 'year').endOf('year')],
  },
  {
    label: "Last Half Year",
    value: [
      dayjs().subtract(6, 'month').startOf('month'),
      dayjs().subtract(1, 'day').endOf('day')
    ],
  }
];

/**
 * Format a date range for Cube.js queries
 * @param dateRange Array of dayjs objects [start, end]
 * @returns Array of formatted date strings in YYYY-MM-DD format
 */
export const formatDateRangeForQuery = (dateRange: DateRange): [string, string] => {
  return [
    dateRange[0].format("YYYY-MM-DD"),
    dateRange[1].format("YYYY-MM-DD")
  ];
};

/**
 * Custom hook for debouncing a date range
 * @param value The date range to debounce
 * @param delay Delay in milliseconds
 * @returns Debounced date range
 */
export const useDateRangeDebounce = (value: DateRange, delay: number): DateRange => {
  const [debouncedValue, setDebouncedValue] = useState<DateRange>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Custom hook for managing date range state with optional debouncing
 * 
 * @param initialDateRange Initial date range
 * @param debounceMs Optional debounce delay in milliseconds
 * @returns An object with the date range, setter, and debounced value (if debouncing enabled)
 */
export const useDateRange = (
  initialDateRange: DateRange,
  debounceMs: number = 0
) => {
  const [dateRange, setDateRange] = useState<DateRange>(initialDateRange);
  
  // Always call the hook, but only use its result when debounceMs > 0
  const debouncedValue = useDateRangeDebounce(dateRange, debounceMs);
  const debouncedDateRange = debounceMs > 0 ? debouncedValue : dateRange;
  
  // Handler for date range changes
  const handleDateChange = (newDateRange: DateRange) => {
    setDateRange(newDateRange);
  };
  
  // Formatted version for queries
  const formattedDateRange = formatDateRangeForQuery(debouncedDateRange);
  
  return {
    dateRange,
    setDateRange,
    handleDateChange,
    debouncedDateRange,
    formattedDateRange
  };
};